// KDE Chart: renders pre-computed kernel density estimate on canvas.
// KDE is computed server-side (F*-verified Epanechnikov kernel with
// half-life weighting); JS only handles rendering and animation.

import gsap from "gsap";
import { bisector } from "d3-array";
import { scaleLinear } from "d3-scale";
import { line as d3line } from "d3-shape";
import type { LiveViewHook, CiPushEventWire } from "../types";
import { formatDuration, kdeMedianIndex, lerp, safeMax } from "./helpers";
import { morphDensities } from "./kde-morph";
// Wire type + validator are paradox-generated from `ci-types.dox`
// (`type KdeData`). The local interface has been deleted — the
// JSON.parse boundary in `mounted()` now runs through validKdeData
// so a malformed `data-kde` seed throws once instead of silently
// rendering a corrupted curve.
import { validKdeData, type KdeData } from "../../../../.dox/ci";

// Pre-built bisector — finds the closest grid index to a target x-domain value
// in O(log n). Used by handleKdeHover to replace a linear nearest-neighbour
// scan over every grid bin. Wrapped in an arrow so the typescript-eslint
// `unbound-method` rule doesn't fire on a stray method reference — the
// bisector's `center` closes over its own state and is safe to call this way.
const xBisector = bisector<number, number>((d) => d);
const xBisect = (a: ArrayLike<number>, v: number) => xBisector.center(a, v);

interface KdeHook extends LiveViewHook<CiPushEventWire> {
  draw(immediate?: boolean): void;
  // Cached "old" snapshot kept on the hook so morphKde can interpolate
  // from the previous KDE shape; updated on morph completion.
  _lastKde?: KdeData;
  // Current KDE (the most recent payload). The hover overlay reads from
  // here on every mousemove. Stored as a hook field rather than a
  // `data-kde` attribute on `this.el` because `phx-update="ignore"`
  // still strips client-set data-* attrs whenever morphdom re-renders
  // a parent — the hover would then return early and the chart
  // appeared to stop responding to mouse input.
  _currentKde?: KdeData;
  _drawTween?: gsap.core.Tween;
  _morphTween?: gsap.core.Tween;
  _resizeObserver?: ResizeObserver;
  _lastW?: number;
  _lastH?: number;
  // Hover
  _overlay?: HTMLCanvasElement;
  _hoverPos?: { x: number; y: number };
  _hoveredIdx?: number;
  _hoverTween?: gsap.core.Tween;
  _boundMouseMove?: (e: MouseEvent) => void;
  _boundMouseLeave?: () => void;
}

function medianSecondsOf(kde: KdeData): number {
  const { grid, densities } = kde;
  if (grid.length === 0) return 0;
  const idx = Math.min(kdeMedianIndex(densities), grid.length - 1);
  return grid[idx]! / 1000;
}

// Morph KDE densities: tween each density value from old to new.
// If grid lengths differ, re-sample old grid via linear interpolation.
function morphKde(hook: KdeHook): void {
  const canvas = hook.el.querySelector("canvas");
  if (!canvas) {
    hook.draw();
    return;
  }

  const newKde = hook._currentKde;
  if (!newKde) return;
  const oldKde = hook._lastKde ?? newKde;

  if (newKde.grid.length < 2) {
    hook.draw();
    return;
  }

  // Density interpolation lives in kde-morph.ts — same shape, but pure,
  // so the math is testable without gsap or a canvas.
  const newDensities = newKde.densities;
  const grid = newKde.grid;
  const xs = grid.map((ms) => ms / 1000);
  const oldMedianS = medianSecondsOf(oldKde);
  const newMedianS = medianSecondsOf(newKde);

  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const needsResize = canvas.width !== w * dpr || canvas.height !== h * dpr;
  if (needsResize) {
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  if (needsResize) ctx.scale(dpr, dpr);

  const margin = { top: 12, right: 12, bottom: 24, left: 0 };
  const innerW = w - margin.left - margin.right;
  const innerH = h - margin.top - margin.bottom;
  // .nice() extends the domain to a friendly tick boundary so xScale.ticks()
  // naturally produces a rightmost label that lines up with the visible edge
  // of the chart — replaces the manual `xTicks.push(domainMax)` below.
  const tickCount = Math.max(6, Math.floor(innerW / 50));
  const xScale = scaleLinear()
    .domain([xs[0]!, xs[xs.length - 1]!])
    .range([margin.left, margin.left + innerW])
    .nice(tickCount);
  const oldXs = oldKde.grid.map((ms) => ms / 1000);
  const oldXScale = scaleLinear()
    .domain([oldXs[0]!, oldXs[oldXs.length - 1]!])
    .range([margin.left, margin.left + innerW])
    .nice(tickCount);
  const oldMedianX = oldXScale(oldMedianS);
  const newMedianX = xScale(newMedianS);

  if (hook._drawTween) {
    hook._drawTween.kill();
    delete hook._drawTween;
  }
  if (hook._morphTween) hook._morphTween.kill();
  const proxy = { t: 0 };
  hook._morphTween = gsap.fromTo(
    proxy,
    { t: 0 },
    {
      t: 1,
      duration: 0.6,
      ease: "power2.inOut",
      onUpdate: () => {
        const t = proxy.t;
        const interpD = morphDensities(oldKde.densities, newDensities, t);
        // safeMax(..., 1e-9) protects the y-domain denominator against
        // div-by-zero when every density is 0 (start-of-life or all-cached).
        const maxDensity = safeMax(interpD, 1e-9);
        const yScale = scaleLinear()
          .domain([0, maxDensity * 1.1])
          .range([margin.top + innerH, margin.top]);

        ctx.clearRect(0, 0, w, h);
        ctx.save();
        ctx.beginPath();
        ctx.rect(margin.left, margin.top, innerW, innerH);
        ctx.clip();

        // d3-shape.line emits canvas moveTo/lineTo via the context — same
        // ops as the hand-rolled loop, but the scale wiring lives in one
        // declarative chain and matches the static draw() path below.
        const curve = d3line<number>()
          .x((_, i) => xScale(xs[i]!))
          .y((d) => yScale(d))
          .context(ctx);
        ctx.beginPath();
        curve(interpD);
        ctx.strokeStyle = "rgba(0, 191, 255, 0.8)";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Median line: interpolate in pixel space so it stays where it was
        // rendered in the previous frame even if the x-domain changed.
        const medianS = lerp(oldMedianS, newMedianS, t);
        const medianX = lerp(oldMedianX, newMedianX, t);
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = "rgba(255, 255, 255, 1)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(medianX, margin.top);
        ctx.lineTo(medianX, margin.top + innerH);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();

        // Median label
        ctx.fillStyle = "rgba(255, 255, 255, 1)";
        ctx.font = "10px 'DejaVu Mono', monospace";
        ctx.textAlign = medianX > margin.left + innerW / 2 ? "right" : "left";
        const labelX = medianX + (medianX > margin.left + innerW / 2 ? -4 : 4);
        ctx.fillText(
          `median ${formatDuration(medianS)}`,
          labelX,
          margin.top + 10,
        );

        // X-axis labels — domain was extended by .nice() above so the
        // rightmost tick already sits at the visible edge.
        ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
        ctx.textAlign = "center";
        for (const tick of xScale.ticks(tickCount)) {
          const px = xScale(tick);
          if (px > margin.left + innerW - 20) ctx.textAlign = "right";
          else if (px < margin.left + 20) ctx.textAlign = "left";
          else ctx.textAlign = "center";
          ctx.fillText(formatDuration(tick), px, h - 4);
        }
      },
      onComplete: () => {
        hook._lastKde = { grid: grid.slice(), densities: newDensities.slice() };
      },
    },
  );
}

function handleKdeHover(hook: KdeHook, mouseX: number): void {
  const overlay = hook._overlay;
  const mainCanvas = hook.el.querySelector<HTMLCanvasElement>(
    "canvas:not(.kde-overlay)",
  );
  if (!overlay || !mainCanvas) return;

  const kdeData = hook._currentKde;
  if (!kdeData) return;
  if (kdeData.grid.length < 2) return;

  const xs = kdeData.grid.map((ms) => ms / 1000);
  const w = mainCanvas.clientWidth;
  const h = mainCanvas.clientHeight;
  const dpr = window.devicePixelRatio || 1;

  const margin = { top: 12, right: 12, bottom: 24, left: 0 };
  const innerW = w - margin.left - margin.right;
  const innerH = h - margin.top - margin.bottom;

  const tickCount = Math.max(6, Math.floor(innerW / 50));
  const xScale = scaleLinear()
    .domain([xs[0]!, xs[xs.length - 1]!])
    .range([margin.left, margin.left + innerW])
    .nice(tickCount);
  const maxDensity = safeMax(kdeData.densities);
  const yScale = scaleLinear()
    .domain([0, maxDensity * 1.1])
    .range([margin.top + innerH, margin.top]);

  // Find nearest grid point. xs is monotonic ascending (KDE grid is evaluated
  // on a regular grid), so bisector.center is O(log n) — replaces the linear
  // scan that touched every bin per mousemove.
  const targetXDomain = xScale.invert(mouseX);
  const nearestIdx = xBisect(xs, targetXDomain);
  if (nearestIdx === hook._hoveredIdx) return;
  hook._hoveredIdx = nearestIdx;

  const targetX = xScale(xs[nearestIdx]!);
  const targetY = yScale(kdeData.densities[nearestIdx]!);
  const label = formatDuration(xs[nearestIdx]!);

  if (overlay.width !== w * dpr || overlay.height !== h * dpr) {
    overlay.width = w * dpr;
    overlay.height = h * dpr;
  }

  if (!hook._hoverPos) hook._hoverPos = { x: targetX, y: targetY };
  if (hook._hoverTween) hook._hoverTween.kill();

  hook._hoverTween = gsap.to(hook._hoverPos, {
    x: targetX,
    y: targetY,
    duration: 0.12,
    ease: "power2.out",
    onUpdate: () => {
      const ctx = overlay.getContext("2d");
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const pos = hook._hoverPos!;

      // Vertical dashed line
      ctx.beginPath();
      ctx.moveTo(pos.x, margin.top);
      ctx.lineTo(pos.x, margin.top + innerH);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Highlight dot on curve
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0, 191, 255, 1)";
      ctx.fill();

      // Tooltip
      ctx.font = "9px 'DejaVu Mono', monospace";
      const ttw = ctx.measureText(label).width + 8;
      const tth = 14;
      let tx = pos.x - ttw / 2;
      let ty = pos.y - tth - 8;
      tx = Math.max(1, Math.min(w - ttw - 1, tx));
      if (ty < 1) ty = pos.y + 8;

      ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
      ctx.beginPath();
      ctx.roundRect(tx, ty, ttw, tth, 3);
      ctx.fill();

      ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, tx + ttw / 2, ty + tth / 2);

      ctx.restore();
    },
  });
}

function clearKdeHover(hook: KdeHook): void {
  if (hook._hoverTween) {
    hook._hoverTween.kill();
    delete hook._hoverTween;
  }
  delete hook._hoveredIdx;
  delete hook._hoverPos;
  const ctx = hook._overlay?.getContext("2d");
  if (ctx && hook._overlay) {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, hook._overlay.width, hook._overlay.height);
    ctx.restore();
  }
}

export const KdeChart = {
  mounted(this: KdeHook) {
    const initCanvas = this.el.querySelector("canvas");
    if (initCanvas) {
      this._lastW = initCanvas.clientWidth;
      this._lastH = initCanvas.clientHeight;
    }

    // Hover overlay
    this.el.style.position = "relative";
    const overlay = document.createElement("canvas");
    overlay.className = "kde-overlay";
    overlay.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none";
    this.el.appendChild(overlay);
    this._overlay = overlay;

    this._boundMouseMove = (e: MouseEvent) => {
      const rect = this.el.getBoundingClientRect();
      handleKdeHover(this, e.clientX - rect.left);
    };
    this._boundMouseLeave = () => {
      clearKdeHover(this);
    };
    this.el.addEventListener("mousemove", this._boundMouseMove);
    this.el.addEventListener("mouseleave", this._boundMouseLeave);

    this._resizeObserver = new ResizeObserver(() => {
      const canvas = this.el.querySelector("canvas");
      if (!canvas) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === this._lastW && h === this._lastH) return;
      this._lastW = w;
      this._lastH = h;
      if (this._morphTween) this._morphTween.kill();
      this.draw(true);
    });
    this._resizeObserver.observe(this.el);

    // Server-rendered seed: the template never sets `data-kde`, so this
    // is a forward-compat hook for the day it does. Parse once into the
    // hook field and discard the dataset entry (morphdom would wipe it
    // on the next parent re-render anyway — see the `_currentKde`
    // comment above).
    const seedRaw = this.el.dataset["kde"];
    if (seedRaw) {
      try {
        this._currentKde = validKdeData(JSON.parse(seedRaw));
        delete this.el.dataset["kde"];
        this.draw();
      } catch {
        // Malformed seed (JSON parse OR validator) — ignore; the
        // first event will provide data. Same swallow-and-continue
        // posture the original try/catch had on JSON.parse, now
        // extended to validator throws.
      }
    }

    this.handleEvent("sparklineUpdate", (raw: unknown) => {
      const payload = raw as Record<string, unknown>;
      const kde = payload["kde"];
      if (!kde) return;
      this._currentKde = kde as KdeData;
      // morphKde / draw kill any in-flight tween before starting a new
      // one, so back-to-back events naturally coalesce at the tween
      // layer — no setTimeout debounce needed. The previous 100 ms
      // setTimeout violated the "events over timers" rule AND meant
      // any event flow faster than 100 ms cleared the timer before
      // firing, so the morph never ran.
      if (this._lastKde) morphKde(this);
      else this.draw();
    });
  },
  updated() {},

  draw(this: KdeHook, immediate = false) {
    const canvas = this.el.querySelector("canvas");
    if (!canvas) return;

    if (this._drawTween) {
      this._drawTween.kill();
      delete this._drawTween;
    }
    if (this._morphTween) {
      this._morphTween.kill();
      delete this._morphTween;
    }

    const kdeData = this._currentKde;
    if (!kdeData) return;
    const { grid, densities } = kdeData;

    // Cache for next morph
    this._lastKde = { grid: grid.slice(), densities: densities.slice() };

    if (grid.length < 2) {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.scale(dpr, dpr);
      this._lastW = canvas.clientWidth;
      this._lastH = canvas.clientHeight;
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.font = "11px 'DejaVu Mono', monospace";
      ctx.textAlign = "center";
      ctx.fillText(
        "Not enough data",
        canvas.clientWidth / 2,
        canvas.clientHeight / 2,
      );
      return;
    }

    const xs = grid.map((ms) => ms / 1000);

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    this._lastW = w;
    this._lastH = h;

    const margin = { top: 12, right: 12, bottom: 24, left: 0 };
    const innerW = w - margin.left - margin.right;
    const innerH = h - margin.top - margin.bottom;

    // .nice() extends the domain to a friendly tick boundary so the rightmost
    // tick lands at the visible edge without the manual `xTicks.push(domainMax)`
    // we used to need.
    const tickCount = Math.max(6, Math.floor(innerW / 50));
    const xScale = scaleLinear()
      .domain([xs[0]!, xs[xs.length - 1]!])
      .range([margin.left, margin.left + innerW])
      .nice(tickCount);

    const maxDensity = safeMax(densities, 1e-9);
    const yScale = scaleLinear()
      .domain([0, maxDensity * 1.1])
      .range([margin.top + innerH, margin.top]);

    // Median: point where cumulative density crosses 50%.
    const medianIdx = Math.min(kdeMedianIndex(densities), xs.length - 1);
    const medianS = xs[medianIdx]!;

    // d3-shape.line w/ canvas context — same moveTo/lineTo ops as the inline
    // loop, but the (x, y) mapping is declared once and shared with morphKde.
    const curve = d3line<number>()
      .x((_, i) => xScale(xs[i]!))
      .y((d) => yScale(d))
      .context(ctx);

    const drawCurve = () => {
      ctx.beginPath();
      curve(densities);
      ctx.strokeStyle = "rgba(0, 191, 255, 0.8)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    };

    const drawLabels = (a: number) => {
      // Median line
      const medianX = xScale(medianS);
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = `rgba(255, 255, 255, ${a})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(medianX, margin.top);
      ctx.lineTo(medianX, margin.top + innerH);
      ctx.stroke();
      ctx.setLineDash([]);

      // Median label
      ctx.fillStyle = `rgba(255, 255, 255, ${a})`;
      ctx.font = "10px 'DejaVu Mono', monospace";
      ctx.textAlign = medianX > margin.left + innerW / 2 ? "right" : "left";
      const labelX = medianX + (medianX > margin.left + innerW / 2 ? -4 : 4);
      ctx.fillText(
        `median ${formatDuration(medianS)}`,
        labelX,
        margin.top + 10,
      );

      // X-axis labels — domain was niced above so xScale.ticks() already
      // produces an edge-aligned rightmost tick.
      ctx.fillStyle = `rgba(255, 255, 255, ${0.7 * a})`;
      ctx.textAlign = "center";
      for (const tick of xScale.ticks(tickCount)) {
        const px = xScale(tick);
        if (px > margin.left + innerW - 20) ctx.textAlign = "right";
        else if (px < margin.left + 20) ctx.textAlign = "left";
        else ctx.textAlign = "center";
        ctx.fillText(formatDuration(tick), px, h - 4);
      }
    };

    if (immediate) {
      ctx.clearRect(0, 0, w, h);
      drawCurve();
      drawLabels(1);
      return;
    }

    const proxy = { t: 0 };
    ctx.clearRect(0, 0, w, h);

    // Wipe duration must stay below `JobDetailPanel`'s 0.5 s open
    // animation. Otherwise the panel finishes revealing before the
    // clip-rect reaches `innerW`, and the right edge of the canvas is
    // visible but unpainted — looks like the curve is clipped on the
    // right. Using `power2.out` to match the panel's ease keeps the two
    // in lockstep when they happen to overlap.
    this._drawTween = gsap.fromTo(
      proxy,
      { t: 0 },
      {
        t: 1,
        duration: 0.4,
        ease: "power2.out",
        onUpdate: () => {
          ctx.clearRect(0, 0, w, h);
          ctx.save();
          ctx.beginPath();
          ctx.rect(margin.left, 0, innerW * proxy.t, h);
          ctx.clip();
          drawCurve();
          ctx.restore();
        },
        onComplete: () => {
          const labelProxy = { alpha: 0 };
          this._drawTween = gsap.to(labelProxy, {
            alpha: 1,
            duration: 0.4,
            ease: "power2.inOut",
            onUpdate: () => {
              ctx.clearRect(0, 0, w, h);
              drawCurve();
              drawLabels(labelProxy.alpha);
            },
            onComplete: () => {
              delete this._drawTween;
            },
          });
        },
      },
    );
  },

  destroyed(this: KdeHook) {
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._drawTween) this._drawTween.kill();
    if (this._morphTween) this._morphTween.kill();
    if (this._boundMouseMove)
      this.el.removeEventListener("mousemove", this._boundMouseMove);
    if (this._boundMouseLeave)
      this.el.removeEventListener("mouseleave", this._boundMouseLeave);
    if (this._overlay) this._overlay.remove();
    clearKdeHover(this);
  },
};
